import BigNumber from 'bignumber.js'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'

export function calculateHealthFactor(
  collateralAmount: NormalizedUnitNumber,
  collateralExchangeRate: Percentage | number | NormalizedUnitNumber,
  borrowedAmount: NormalizedUnitNumber,
  maxLtv: Percentage,
): BigNumber {
  const exchangeRate =
    typeof collateralExchangeRate === 'number' ? collateralExchangeRate : collateralExchangeRate.toNumber()

  const collateralValueInBorrowAsset = collateralAmount.toNumber() * exchangeRate

  if (borrowedAmount.toNumber() <= 0 || maxLtv.toNumber() <= 0) {
    return new BigNumber(Number.POSITIVE_INFINITY)
  }

  const rawHf = collateralValueInBorrowAsset / borrowedAmount.toNumber() / maxLtv.toNumber()
  return new BigNumber(rawHf)
}

export function calculateIsolatedHealthFactor(
  collateralAmount: NormalizedUnitNumber,
  collateralUnitPriceUsd: Percentage | number | NormalizedUnitNumber,
  borrowedAmount: NormalizedUnitNumber,
  maxLtv: Percentage,
): BigNumber {
  if (borrowedAmount.toNumber() <= 0 || maxLtv.toNumber() <= 0) {
    return new BigNumber(Number.POSITIVE_INFINITY)
  }

  return collateralAmount.multipliedBy(collateralUnitPriceUsd).multipliedBy(maxLtv).dividedBy(borrowedAmount)
}

export function calculateUpdatedHealthFactor({
  collateralAmount,
  collateralExchangeRate,
  borrowedAmount,
  maxLtv,
  collateralChange = 0,
  borrowChange = 0,
  isCollateralIncrease = true,
  isBorrowIncrease = true,
  isIsolated = false,
}: {
  collateralAmount: NormalizedUnitNumber
  collateralExchangeRate: Percentage | number | NormalizedUnitNumber
  borrowedAmount: NormalizedUnitNumber
  maxLtv: Percentage
  collateralChange?: number
  borrowChange?: number
  isCollateralIncrease?: boolean
  isBorrowIncrease?: boolean
  isIsolated?: boolean
}): BigNumber {
  const updatedCollateralAmount = NormalizedUnitNumber(
    isCollateralIncrease
      ? collateralAmount.toNumber() + collateralChange
      : Math.max(0, collateralAmount.toNumber() - collateralChange),
  )

  const updatedBorrowedAmount = NormalizedUnitNumber(
    isBorrowIncrease ? borrowedAmount.toNumber() + borrowChange : Math.max(0, borrowedAmount.toNumber() - borrowChange),
  )

  return isIsolated
    ? calculateIsolatedHealthFactor(updatedCollateralAmount, collateralExchangeRate, updatedBorrowedAmount, maxLtv)
    : calculateHealthFactor(updatedCollateralAmount, collateralExchangeRate, updatedBorrowedAmount, maxLtv)
}
